import { z } from 'zod';
import { customFieldsClient } from 'services/api';
import { trackError } from '@proposify/frontend-observability';
import { Endpoints } from 'utils/endpoints';
import { NetworkValidationError } from 'services/errors/NetworkValidationError';
import type { VariablesCustomFieldsRepository } from './VariablesCustomFieldsRepository';
import type { CustomFieldDto, CustomFieldInputDto } from './schemas';
import { customFieldDtoSchema } from './schemas';

export class ApiVariablesCustomFieldsRepository implements VariablesCustomFieldsRepository {
  public async getCustomFields(): Promise<CustomFieldDto[]> {
    let response;
    try {
      response = await customFieldsClient.get(Endpoints.customFields);
    } catch (e) {
      trackError(e instanceof Error ? e : new Error(String(e)), {
        endpoint: Endpoints.customFields,
        errorFrom: 'editor-remote',
      });

      throw new Error('Error fetching custom fields');
    }

    try {
      return z.array(customFieldDtoSchema).parse(response);
    } catch (e) {
      const validationError = new NetworkValidationError('getCustomFields response schema validation failed', {
        cause: e instanceof Error ? e : new Error(String(e)),
      });

      trackError(validationError, {
        endpoint: Endpoints.customFields,
        errorFrom: 'editor-remote',
        validationErrors: e instanceof z.ZodError ? e.format() : undefined,
      });

      throw new Error('Error fetching custom fields');
    }
  }

  public async getCustomFieldById(id: string): Promise<CustomFieldDto> {
    let response;
    try {
      response = await customFieldsClient.get(Endpoints.customFieldById(id));
    } catch (e) {
      trackError(e instanceof Error ? e : new Error(String(e)), {
        endpoint: Endpoints.customFieldById(id),
        errorFrom: 'editor-remote',
      });

      throw new Error('Error fetching custom field');
    }

    try {
      return customFieldDtoSchema.parse(response);
    } catch (e) {
      const validationError = new NetworkValidationError('getCustomFieldById response schema validation failed', {
        cause: e instanceof Error ? e : new Error(String(e)),
      });

      trackError(validationError, {
        endpoint: Endpoints.customFieldById(id),
        errorFrom: 'editor-remote',
        validationErrors: e instanceof z.ZodError ? e.format() : undefined,
      });

      throw new Error('Error fetching custom field');
    }
  }

  public async createCustomField(payload: CustomFieldInputDto): Promise<CustomFieldDto> {
    let response;
    try {
      response = await customFieldsClient.post(Endpoints.customFields, payload);
    } catch (e) {
      trackError(e instanceof Error ? e : new Error(String(e)), {
        endpoint: Endpoints.customFields,
        errorFrom: 'editor-remote',
        payload: JSON.stringify(payload),
      });

      throw new Error('Error creating custom field');
    }

    try {
      return customFieldDtoSchema.parse(response);
    } catch (e) {
      const validationError = new NetworkValidationError('createCustomField response schema validation failed', {
        cause: e instanceof Error ? e : new Error(String(e)),
      });

      trackError(validationError, {
        endpoint: Endpoints.customFields,
        errorFrom: 'editor-remote',
        payload: JSON.stringify(payload),
        validationErrors: e instanceof z.ZodError ? e.format() : undefined,
      });

      throw new Error('Error creating custom field');
    }
  }

  public async updateCustomField(id: string, payload: CustomFieldInputDto): Promise<CustomFieldDto> {
    let response;
    try {
      response = await customFieldsClient.put(Endpoints.customFieldById(id), payload);
    } catch (e) {
      trackError(e instanceof Error ? e : new Error(String(e)), {
        endpoint: Endpoints.customFieldById(id),
        errorFrom: 'editor-remote',
        payload: JSON.stringify(payload),
      });

      throw new Error('Error updating custom field');
    }

    try {
      return customFieldDtoSchema.parse(response);
    } catch (e) {
      const validationError = new NetworkValidationError('updateCustomField response schema validation failed', {
        cause: e instanceof Error ? e : new Error(String(e)),
      });

      trackError(validationError, {
        endpoint: Endpoints.customFieldById(id),
        errorFrom: 'editor-remote',
        payload: JSON.stringify(payload),
        validationErrors: e instanceof z.ZodError ? e.format() : undefined,
      });

      throw new Error('Error updating custom field');
    }
  }

  public async deleteCustomField(id: string): Promise<void> {
    try {
      await customFieldsClient.delete(Endpoints.customFieldById(id));
    } catch (e) {
      trackError(e instanceof Error ? e : new Error(String(e)), {
        endpoint: Endpoints.customFieldById(id),
        errorFrom: 'editor-remote',
      });

      throw new Error('Error deleting custom field');
    }
  }
}
